import { createSelector } from 'reselect';
import {
	get, reduce, random, omit, isEmpty, isFunction
} from 'lodash';
import { getSearchViewPixel, getSearchLandingViewPixel } from '@sparefoot/react-core/utils/tracking';

import {
	storageStackedUnitBuckets,
	vehicleStackedUnitBuckets
} from 'config/units';
import faqByPage from 'config/faqByPage';
import { withinCallCenterHours } from 'utils/time/withinCallCenterHours';
import { getQueryParams } from 'store/selectors/application';
import buildSearchLink from 'utils/search/buildSearchLink';

const getFacilityUnits = (facilityUnits, units) => reduce(
	[
		...storageStackedUnitBuckets,
		...vehicleStackedUnitBuckets,
		'recommendedUnit'
	],
	(allUnits, size) => ({
		...allUnits,
		[size]: units[facilityUnits[size]]
	}),
	{ ...facilityUnits }
);

export const getSearchFacilities = createSelector(
	(state) => state.search.facilities,
	(state) => state.facilities.byId,
	(state) => state.units.byId,
	(state) => state.reviews.byId,
	(state) => state.search.page,
	(state) => state.search.listingsPerPage,
	(facilityIds, facilities, units, reviews, page, listingsPerPage) => facilityIds.map(
		(facilityId, index) => {
			const facility = facilities[facilityId];

			const facilityUnits = get(facility, 'units', {});

			const highestReview = get(reviews, facility.highestReview, null);

			const bestReviews = get(facility, 'bestReviews', [])
			.slice()
			.reverse()
			.map((reviewId) => reviews[reviewId]);

			return {
				...facility,
				index: index + 1,
				position: (index + 1) + ((page - 1) * listingsPerPage),
				units: getFacilityUnits(facilityUnits, units),
				bestReviews,
				highestReview
			};
		}
	)
);

export const getRecentReservations = createSelector(
	(state) => state.search.locationHash,
	(state, props) => props.cookies,
	(hash, cookies) => {
		const cookieStr = `rr_${hash}`;
		const locationCookie = cookies.get(cookieStr);
		if (locationCookie) {
			return parseInt(locationCookie);
		}
		const reservationValue = random(10, 50);
		cookies.set(cookieStr, reservationValue, { path: '/' });
		return reservationValue;
	}
);

export const getStorageTypeLinks = createSelector(
	(state) => state.search.query,
	(state) => state.search.cityUrl,
	(queryParams, cityUrl) => {
		if (!queryParams.pageType) {
			return {
				storage: buildSearchLink(omit(queryParams, ['type', 'vehicle', 'page']), true),
				vehicle: buildSearchLink({ ...omit(queryParams, ['page']), type: 'vehicle' }, true)
			};
		}
		const slash = cityUrl && cityUrl.slice(-1) === '/' ? '' : '/';
		return {
			storage: cityUrl,
			vehicle: `${cityUrl}${slash}car-storage/`
		};
	}
);

export const getSearchLocationText = createSelector(
	(state) => state.search.query,
	(state) => get(state.search, 'content.location'),
	(state) => state.search.location,
	(query, contentLocation, searchLocation) => {
		if (query.pageType) {
			if (contentLocation) {
				return `${contentLocation.city}, ${contentLocation.state}`;
			}
			return `${searchLocation.city}, ${searchLocation.state}`;
		}
		return query.location;
	}
);

export const amenityCLPRedirect = createSelector(
	(state) => state.search,
	({ pageType, totalListings, cityUrl, statusCode }) => {
		if (pageType === 'amenityLandingPage' && !totalListings) {
			return { statusCode: 301, redirectURL: cityUrl };
		}

		return { statusCode };
	}
);

export const vipCardSelector = createSelector(
	getSearchFacilities,
	(state) => state.search,
	(facilities, search) => {
		const { page, searchType, nearbyFacilities, vipAccounts } = search;
		const isFirstPage = page === 1;
		const firstFacility = isFirstPage && facilities.length && facilities[0];
		const firstFacilityAccountId = get(firstFacility, 'account.id');
		const vipAccount = get(vipAccounts, firstFacilityAccountId, false);
		const vipFacilityId = vipAccount ? firstFacility.id : null;
		const firstListingIsVipElegible = isFirstPage && searchType === 'storage' && !!vipAccount;
		return {
			firstListingIsVipElegible,
			vipAccount,
			firstFacilityAccountId,
			nearbyFacilities: nearbyFacilities.filter(({ id }) => id !== vipFacilityId)
		};
	}
);

export const faqContentSelector = createSelector(
	(state) => state.search,
	({ location, content, query }) => {
		// Reading from seoBodyJson first
		const seoBodyJson = get(content, 'seoBodyJson', []) || [];
		let faq = [];
		seoBodyJson.forEach((item) => {
			if (item.type === 'faq_section') {
				faq = faq.concat(item.value.map((faqItem) => ({ copy: faqItem.a, title: faqItem.q })));
			}
		});

		if (faq.length) {
			return {
				faqContent: faq,
				faqShowing: true
			};
		}

		let functionPath = get(query, 'pageType');

		if (functionPath === 'vehicleLandingPage') {
			functionPath += `.${get(query, 'vehicle')}`;
		} else if (functionPath === 'amenityLandingPage') {
			functionPath += `.${get(query, 'subPageType')}`;
		}

		const faqFunction = get(faqByPage, functionPath, []);

		if (isFunction(faqFunction)) {
			const { city, state } = location;
			const avgPrices = get(content, 'pricingStats.averagePrices');
			const faqContent = faqFunction(city, state, avgPrices);

			return {
				faqContent,
				faqShowing: !!faqContent.length
			};
		}
		return {};
	}
);

export const headlineSelector = createSelector(
	(state) => state.search,
	({ headline, content }) => {
		const contentTitle = get(content, 'contentTitle');
		const returnValue = {
			headline: contentTitle || headline || 'Self-Storage',
			subtitle: get(content, 'contentSubtitle')
		};

		return returnValue;
	}
);

export default createSelector(
	getSearchFacilities,
	getStorageTypeLinks,
	getSearchLocationText,
	getQueryParams,
	getRecentReservations,
	(state) => state.search,
	(state) => state.phone,
	(state) => state.user,
	amenityCLPRedirect,
	faqContentSelector,
	(state) => state.search.redirectURL,
	(state) => state.app.baseUrl,
	(state) => state.app.cloudfrontUrl,
	headlineSelector,
	(
		facilities,
		storageTypeUrls,
		searchLocationText,
		queryParams,
		recentReservations,
		search,
		phone,
		user,
		amenityCLPRedirectProps,
		faqContent,
		redirectURL,
		baseUrl,
		cloudfrontUrl,
		titles
	) => ({
		facilities,
		baseUrl,
		cloudfrontUrl,
		cityUrl: search.cityUrl,
		errorMessage: search.error,
		facetCounts: search.facetCounts,
		filtersShowing: search.filtersShowing,
		stateUrl: search.stateUrl,
		headline: titles.headline,
		subtitle: titles.subtitle,
		loading: search.loading,
		searchLocation: search.location,
		meta: search.meta,
		page: search.page,
		phone: phone.headerPhone,
		stickyTrayPhoneNumber: phone.stickyTrayPhoneNumber,
		facilityNotFound: !!queryParams.facilityNotFound,
		recentReservations,
		withinCallCenterHours: withinCallCenterHours(),
		visitId: user.visitId,
		searchId: search.searchId,
		searchContent: get(search, 'content', {}),
		searchDividerIndex: search.lastInOriginalSearch,
		searchLocationText,
		searchParams: search.query,
		searchType: search.searchType,
		showInformedSearch: search.query.pageType === 'cityLandingPage' && search.page === 1,
		showRecommendedUnit: !!search.query.sqft || !!search.query.length || !!search.query.vehicleAccommodation,
		staticMapUrl: search.staticMapUrl,
		totalBookings: get(search, 'content.cityTotalBookings', 0),
		totalListings: search.totalListings,
		totalPages: search.totalPages,
		pageType: search.pageType,
		...amenityCLPRedirectProps,
		storageTypeUrls,
		trackingPixel: !isEmpty(search.content) ?
			getSearchLandingViewPixel(search, search.content) :
			getSearchViewPixel(search, search.content),
		...faqContent,
		redirectURL
	})
);
